import { useEffect } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import {
  IconLoader,
  IconArrowLeft,
  IconSelected,
  IconBlock,
  ToggleInput,
  ButtonSecondary,
  ButtonSize,
  IconEdit,
  PermissionEnum,
} from '@ui/src';
import { useAppSelector } from '@store';
import {
  useLazyGetApplicationSchemeQuery,
  useEditApplicationSchemeVisibilityMutation,
  useEditApplicationSchemeActiveMutation,
} from '../store/services/application-shemes';
import ROUTES from '../../../routes';

import cn from './styles.module.css';

const ApplicationScheme = () => {
  const { schemeId } = useParams();

  const {
    settings: {
      applicationSchemes: { scheme },
    },
    administrationDictionaries: { activePermissions },
  } = useAppSelector((state) => state);

  const [getApplicationScheme, { isLoading, isFetching }] =
    useLazyGetApplicationSchemeQuery();
  const [editVisibility, { isLoading: requestInProgress }] =
    useEditApplicationSchemeVisibilityMutation();
  const [editActive, { isLoading: activeInProgress }] =
    useEditApplicationSchemeActiveMutation();

  const onChangeVisibility = (visibility: boolean) => {
    editVisibility({
      id: schemeId as string,
      body: {
        visible: visibility,
      },
    });
  };

  const onChangeActive = (active: boolean) => {
    editActive({
      id: schemeId as string,
      body: {
        active,
      },
    });
  };

  useEffect(() => {
    if (schemeId) {
      getApplicationScheme(schemeId);
    }
  }, [schemeId]);

  useEffect(() => {
    let title = 'Settings: Application Scheme';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  let content;

  if (isLoading || isFetching) {
    content = (
      <div className={`${cn.applicationSchemeLoader} ${cn.withBackground}`}>
        <IconLoader />
      </div>
    );
  } else {
    content = (
      <div className={cn.applicationScheme}>
        <NavLink to={ROUTES.APPLICATIONS_LIST} className={cn.backLink}>
          <IconArrowLeft width={13} height={11} />
          Back
        </NavLink>
        <div className={cn.titleWrapper}>
          <div className={cn.titleLeft}>
            <div className={cn.title}>{scheme?.name}</div>
            <div className={`${cn.activity} ${scheme?.active ? cn.green : ''}`}>
              {scheme?.active ? (
                <IconSelected width={13} height={11} />
              ) : (
                <IconBlock />
              )}
              {scheme?.active ? 'Active' : 'Inactive'}
            </div>
          </div>
          <ToggleInput
            name="visible"
            checked={scheme?.visible}
            title="Visible to clients"
            onChange={(e) => onChangeVisibility(e.target.checked)}
            disabled={
              requestInProgress ||
              !activePermissions?.includes(
                PermissionEnum.APPLICATIONS_SCHEMES_EDIT_ACTION,
              )
            }
          />
        </div>
        <div className={cn.controls}>
          <ButtonSecondary
            size={ButtonSize.SMALL}
            icon={scheme?.active ? <IconBlock /> : <IconSelected />}
            text={scheme?.active ? 'Disable' : 'Activate'}
            onClick={() => onChangeActive(!scheme?.active)}
            disabled={
              activeInProgress ||
              !activePermissions?.includes(
                PermissionEnum.APPLICATIONS_SCHEMES_EDIT_ACTION,
              )
            }
          />
          {activePermissions?.includes(
            PermissionEnum.APPLICATIONS_SCHEMES_EDIT_ACTION,
          ) && (
            <>
              <div className={cn.separator} />
              <ButtonSecondary
                size={ButtonSize.SMALL}
                className={`button-white ${cn.buttonEdit}`}
                icon={<IconEdit />}
                text="Edit"
              />
            </>
          )}
        </div>
      </div>
    );
  }

  return <div className={cn.schemeWrapper}>{content}</div>;
};

export default ApplicationScheme;
